import { useState, useEffect, useCallback } from 'react';
import API from '../Api';

// In-memory cache for products
let productsCache = null;
let productsCacheTime = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Custom hook to fetch and cache active products
 * Prevents duplicate API calls across components
 * @returns {Object} { products, loading, error, refetch }
 */
const useProducts = () => {
	const [products, setProducts] = useState(productsCache || []);
	const [loading, setLoading] = useState(!productsCache);
	const [error, setError] = useState(null);

	const fetchProducts = useCallback(async () => {
		// Check if cache is still valid
		if (productsCache && productsCacheTime) {
			const now = Date.now();
			if (now - productsCacheTime < CACHE_DURATION) {
				setProducts(productsCache);
				setLoading(false);
				return productsCache;
			}
		}

		setLoading(true);
		setError(null);

		try {
			const res = await API.get('product/active/all/for/cart');
			const productsData = res.data.data || [];

			// Update cache
			productsCache = productsData;
			productsCacheTime = Date.now();

			setProducts(productsData);
			setLoading(false);
			return productsData;
		} catch (err) {
			setError(err);
			setLoading(false);

			// Still return cached data if available on error
			if (productsCache) {
				setProducts(productsCache);
				return productsCache;
			}
			return [];
		}
	}, []);

	useEffect(() => {
		fetchProducts();
	}, [fetchProducts]);

	// Manual refetch function to invalidate cache
	const refetch = useCallback(async () => {
		productsCache = null;
		productsCacheTime = null;
		return await fetchProducts();
	}, [fetchProducts]);

	return {
		products,
		loading,
		error,
		refetch
	};
};

// Export function to clear cache (useful for testing or manual invalidation)
export const clearProductsCache = () => {
	productsCache = null;
	productsCacheTime = null;
	
};

export default useProducts;
